import { createFileRoute, useNavigate, useSearch } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { sendTransactionalEmail } from "@/lib/email/send";
import { useAppContexts } from "@/hooks/use-app-context";


type Search = { editId?: string };

export const Route = createFileRoute("/_authenticated/events/new")({
  head: () => ({ meta: [{ title: "Crear evento — Brulo" }] }),
  validateSearch: (s: Record<string, unknown>): Search => ({
    editId: typeof s.editId === "string" ? s.editId : undefined,
  }),
  component: NewEvent,
});

type CafeOption = { id: string; name: string; location: string };

const TIPO_OPTIONS = [
  { v: "running", l: "🏃 Running" },
  { v: "ciclismo", l: "🚴 Ciclismo" },
  { v: "trail", l: "🏔️ Trail" },
  { v: "caminata", l: "🚶 Caminata" },
  { v: "otro", l: "💪 Otro" },
] as const;

function NewEvent() {
  const navigate = useNavigate();
  const { active: activeCtx, ready: ctxReady } = useAppContexts();
  const ctxClubId = ctxReady && activeCtx.kind === "club" ? activeCtx.id : null;
  const ctxCafeId = ctxReady && activeCtx.kind === "cafe" ? activeCtx.id : null;
  const { editId } = useSearch({ from: "/_authenticated/events/new" });
  const isEdit = !!editId;

  const [cafes, setCafes] = useState<CafeOption[]>([]);
  const [cafesLoaded, setCafesLoaded] = useState(false);
  const [clubId, setClubId] = useState<string | null>(null);
  const [clubName, setClubName] = useState<string>("");
  const [inviteOpen, setInviteOpen] = useState(false);
  const [pendingInvite, setPendingInvite] = useState<{ id: string; nombre: string } | null>(null);
  const [form, setForm] = useState({
    name: "",
    tipo_actividad: "" as "" | "running" | "ciclismo" | "trail" | "caminata" | "otro",
    event_date: "",
    event_time: "",
    cafe_id: "",
    meeting_point: "",
    meeting_details: "",
    distance_km: "",
    description: "",
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    (async () => {
      const { data } = await supabase
        .from("cafes")
        .select("id,name,location")
        .order("name");
      setCafes(data ?? []);
      setCafesLoaded(true);

      const { data: u } = await supabase.auth.getUser();
      if (u.user) {
        // El club se toma del contexto activo cuando existe; si no, se resuelve
        // por vínculo de gestión (nunca por profiles.role).
        if (ctxClubId) {
          const { data: club } = await supabase
            .from("clubs")
            .select("id,name")
            .eq("id", ctxClubId)
            .maybeSingle();
          if (club) {
            setClubId(club.id);
            setClubName(club.name);
          }
        } else {
          const { data: link } = await supabase
            .from("club_managers")
            .select("club_id, clubs(id,name)")
            .eq("user_id", u.user.id)
            .limit(1)
            .maybeSingle();
          const club = (link as unknown as { clubs: { id: string; name: string } | null } | null)?.clubs;
          if (club) {
            setClubId(club.id);
            setClubName(club.name);
          }
        }
      }


      if (isEdit && editId) {
        const { data: ev } = await supabase
          .from("events")
          .select("name,tipo_actividad,event_date,event_time,cafe_id,meeting_point,meeting_details,distance_km,description")
          .eq("id", editId)
          .maybeSingle();
        if (ev) {
          const ta = ev.tipo_actividad as string | null;
          const tipo = ta === "ciclismo" || ta === "trail" || ta === "caminata" || ta === "running" || ta === "otro" ? ta : "running";
          setForm({
            name: ev.name ?? "",
            tipo_actividad: tipo as "running" | "ciclismo" | "trail" | "caminata" | "otro",
            event_date: ev.event_date ?? "",
            event_time: ev.event_time ? String(ev.event_time).slice(0, 5) : "",
            cafe_id: ev.cafe_id ?? "",
            meeting_point: ev.cafe_id ? "" : (ev.meeting_point ?? ""),
            meeting_details: ev.meeting_details ?? "",
            distance_km: ev.distance_km != null ? String(ev.distance_km) : "",
            description: ev.description ?? "",
          });
        }
      }
    })();
  }, [isEdit, editId, ctxClubId]);

  const set = (k: string) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) =>
    setForm({ ...form, [k]: e.target.value });

  const hasCafes = cafes.length > 0;

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.tipo_actividad) {
      toast.error("Selecciona el tipo de actividad");
      return;
    }
    setLoading(true);
    const { data: u } = await supabase.auth.getUser();
    if (!u.user) return;

    const selectedCafe = cafes.find((c) => c.id === (ctxCafeId ?? form.cafe_id));
    let meetingPoint = "";
    if (ctxCafeId) {
      meetingPoint = selectedCafe ? `${selectedCafe.name} — ${selectedCafe.location}` : "";
    } else if (pendingInvite) {
      meetingPoint = `Pendiente — ${pendingInvite.nombre}`;
    } else if (hasCafes) {
      meetingPoint = selectedCafe ? `${selectedCafe.name} — ${selectedCafe.location}` : "";
    } else {
      meetingPoint = form.meeting_point;
    }

    if (!meetingPoint) {
      toast.error("Selecciona o escribe un punto de reunión");
      setLoading(false);
      return;
    }

    // El café/club se toma SIEMPRE del contexto activo; el backend re-valida el
    // vínculo de gestión vía RLS (cafe_managers / club_managers).
    const payload = {
      name: form.name,
      tipo_actividad: form.tipo_actividad,
      event_date: form.event_date,
      event_time: form.event_time,
      meeting_point: meetingPoint,
      meeting_details: form.meeting_details || null,
      cafe_id: ctxCafeId ?? (pendingInvite ? null : (hasCafes && form.cafe_id ? form.cafe_id : null)),
      cafe_invitado_id: ctxCafeId ? null : (pendingInvite ? pendingInvite.id : null),
      distance_km: form.distance_km ? parseFloat(form.distance_km) : null,
      description: form.description || null,
    };

    const insertPayload = ctxCafeId
      ? { ...payload, organizer_id: u.user.id, organizador_tipo: "cafeteria", club_id: null }
      : { ...payload, organizer_id: u.user.id, organizador_tipo: "club", club_id: ctxClubId ?? clubId };

    const { error } = isEdit && editId
      ? await supabase.from("events").update(payload).eq("id", editId)
      : await supabase.from("events").insert(insertPayload);

    setLoading(false);
    if (error) {
      toast.error(error.message);
      return;
    }
    toast.success(isEdit ? "Evento actualizado" : "Evento creado");
    navigate({ to: "/dashboard" });
  };

  return (
    <div className="mx-auto max-w-2xl px-4 md:px-6 py-8 md:py-12">
      <p className="text-primary text-xs tracking-[0.3em] uppercase mb-2">{isEdit ? "editar salida" : "nueva salida"}</p>
      <h1 className="font-display text-4xl sm:text-5xl mb-8 md:mb-10">{isEdit ? "Editar evento" : "Crear evento"}</h1>

      <form onSubmit={submit} className="space-y-5 bg-card border border-border rounded-lg p-5 sm:p-8">
        <Field label="Nombre del evento">
          <input required value={form.name} onChange={set("name")} className={inputCls} placeholder="Long run dominical" />
        </Field>

        <Field label="Tipo de actividad">
          <div className="grid grid-cols-2 gap-2">
            {TIPO_OPTIONS.map((opt) => (
              <button
                type="button"
                key={opt.v}
                onClick={() => setForm({ ...form, tipo_actividad: opt.v })}
                className={`py-2.5 px-3 rounded-md border text-sm font-medium ${
                  form.tipo_actividad === opt.v
                    ? "border-primary bg-primary/10 text-primary"
                    : "border-border text-muted-foreground"
                }`}
              >
                {opt.l}
              </button>
            ))}
          </div>
        </Field>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Field label="Fecha">
            <input required type="date" value={form.event_date} onChange={set("event_date")} className={inputCls} />
          </Field>
          <Field label="Hora">
            <input required type="time" value={form.event_time} onChange={set("event_time")} className={inputCls} />
          </Field>
        </div>

        <Field label="Punto de reunión">
          {!cafesLoaded ? (
            <div className={`${inputCls} text-muted-foreground`}>Cargando cafeterías...</div>
          ) : ctxCafeId ? (
            <div className={`${inputCls} text-muted-foreground`}>
              {cafes.find((c) => c.id === ctxCafeId)?.name ?? "Tu cafetería"} — se toma del contexto activo
            </div>
          ) : pendingInvite ? (

            <div className="space-y-2">
              <div className="px-4 py-3 rounded-md border border-yellow-500/40 bg-yellow-500/10 text-sm">
                <span className="inline-block text-[10px] uppercase tracking-[0.2em] font-bold px-2 py-0.5 rounded bg-yellow-500 text-black mr-2">
                  Pendiente
                </span>
                {pendingInvite.nombre}
              </div>
              <button
                type="button"
                onClick={() => setPendingInvite(null)}
                className="text-xs text-muted-foreground underline"
              >
                Quitar y elegir otra opción
              </button>
            </div>
          ) : hasCafes ? (
            <>
              <select required={!pendingInvite} value={form.cafe_id} onChange={set("cafe_id")} className={inputCls}>
                <option value="">Selecciona una cafetería Brulo</option>
                {cafes.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name} — {c.location}
                  </option>
                ))}
              </select>
              <p className="text-xs text-muted-foreground mt-1.5">
                La cafetería recibirá una solicitud de confirmación. Mientras tanto, el evento se publicará con badge "Pendiente de confirmación".
              </p>
              <button
                type="button"
                onClick={() => setInviteOpen(true)}
                className="text-xs text-[oklch(0.7_0.18_25)] hover:underline mt-2 inline-block"
              >
                ¿No encuentras tu café? Invítalo a Brulo →
              </button>
            </>
          ) : (
            <div className="space-y-2">
              <p className="text-sm text-muted-foreground">
                Aún no hay cafeterías Brulo en tu ciudad — puedes escribir la dirección manualmente
              </p>
              <input
                required
                value={form.meeting_point}
                onChange={set("meeting_point")}
                className={inputCls}
                placeholder="Plaza Mayor, junto a la fuente"
              />
              <button
                type="button"
                onClick={() => setInviteOpen(true)}
                className="text-xs text-[oklch(0.7_0.18_25)] hover:underline mt-2 inline-block"
              >
                ¿No encuentras tu café? Invítalo a Brulo →
              </button>
            </div>
          )}
        </Field>

        <Field label="Detalles del punto de encuentro (opcional)">
          <input
            value={form.meeting_details}
            onChange={set("meeting_details")}
            maxLength={150}
            className={inputCls}
            placeholder="Ej: en la entrada principal, sobre la banqueta, toca el timbre..."
          />
          <p className="text-xs text-muted-foreground mt-1">{form.meeting_details.length}/150</p>
        </Field>

        <Field label="Distancia (km)">
          <input type="number" step="0.1" value={form.distance_km} onChange={set("distance_km")} className={inputCls} placeholder="10" />
        </Field>
        <Field label="Notas (opcional)">
          <textarea value={form.description} onChange={set("description") as any} rows={3} className={inputCls} placeholder="Ritmo cómodo, café al final en..." />
        </Field>

        <button
          type="submit"
          disabled={loading}
          className="w-full min-h-12 px-3 py-3 rounded-md bg-primary text-primary-foreground text-sm sm:text-base font-semibold tracking-wide hover:opacity-90 disabled:opacity-50 whitespace-normal break-words text-center leading-tight"
        >
          {loading ? (isEdit ? "Guardando..." : "Creando...") : (isEdit ? "Guardar cambios" : "Publicar evento")}
        </button>
      </form>

      {inviteOpen && (
        <InviteCafeModal
          clubId={clubId}
          clubName={clubName}
          onClose={() => setInviteOpen(false)}
          onInvited={(invite) => {
            setPendingInvite(invite);
            setInviteOpen(false);
          }}
        />
      )}
    </div>
  );
}

function InviteCafeModal({
  clubId,
  clubName,
  onClose,
  onInvited,
}: {
  clubId: string | null;
  clubName: string;
  onClose: () => void;
  onInvited: (invite: { id: string; nombre: string }) => void;
}) {
  const [data, setData] = useState({ nombre: "", telefono: "", email: "", direccion: "" });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!data.nombre || !data.telefono) {
      toast.error("Nombre y teléfono son obligatorios");
      return;
    }
    setSending(true);
    const { data: u } = await supabase.auth.getUser();
    if (!u.user) {
      setSending(false);
      return;
    }
    const { data: inserted, error } = await supabase
      .from("cafes_invitados")
      .insert({
        nombre: data.nombre,
        telefono: data.telefono,
        email: data.email || null,
        direccion: data.direccion || null,
        club_id: clubId,
        organizador_id: u.user.id,
      })
      .select("id,nombre")
      .single();
    if (error || !inserted) {
      toast.error(error?.message ?? "No pudimos enviar la invitación");
      setSending(false);
      return;
    }

    if (data.email) {
      try {
        await sendTransactionalEmail({
          templateName: "cafe-invitation",
          recipientEmail: data.email,
          idempotencyKey: `cafe-invitation-${inserted.id}`,
          templateData: { cafeName: data.nombre, clubName: clubName || "Un club" },
          context: { clubId: clubId ?? undefined },
        });
      } catch (err) {
        console.warn("Email send failed", err);
      }
    }

    setSent(true);
    setSending(false);
    setTimeout(() => onInvited({ id: inserted.id, nombre: inserted.nombre }), 1500);
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={onClose}>
      <div
        className="bg-card border border-border rounded-xl max-w-md w-full p-6 sm:p-8"
        onClick={(e) => e.stopPropagation()}
      >
        {sent ? (
          <div className="text-center py-4">
            <h3 className="font-display text-2xl mb-3">Invitación enviada</h3>
            <p className="text-muted-foreground text-sm">
              Te avisaremos cuando <strong>{data.nombre}</strong> se registre en Brulo.
            </p>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-4">
            <div>
              <h3 className="font-display text-2xl mb-1">Invita a tu café favorito</h3>
              <p className="text-sm text-muted-foreground">
                Cuando se registre podrás elegirlo como punto de reunión
              </p>
            </div>
            <Field label="Nombre del café">
              <input required value={data.nombre} onChange={(e) => setData({ ...data, nombre: e.target.value })} className={inputCls} placeholder="Venerdi Café" />
            </Field>
            <Field label="Teléfono de contacto">
              <input required value={data.telefono} onChange={(e) => setData({ ...data, telefono: e.target.value })} className={inputCls} placeholder="449 123 4567" />
            </Field>
            <Field label="Email de contacto (opcional)">
              <input type="email" value={data.email} onChange={(e) => setData({ ...data, email: e.target.value })} className={inputCls} placeholder="hola@cafe.com" />
            </Field>
            <Field label="Dirección (opcional)">
              <input value={data.direccion} onChange={(e) => setData({ ...data, direccion: e.target.value })} className={inputCls} placeholder="Av. Aguascalientes 100" />
            </Field>
            <div className="flex gap-2 pt-2">
              <button type="button" onClick={onClose} className="flex-1 py-3 rounded-md border border-border text-sm">
                Cancelar
              </button>
              <button
                type="submit"
                disabled={sending}
                className="flex-1 py-3 rounded-md bg-[oklch(0.7_0.18_25)] text-white text-sm font-semibold disabled:opacity-50"
              >
                {sending ? "Enviando..." : "Enviar invitación"}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}

const inputCls =
  "w-full px-4 py-3 min-h-11 bg-input border border-border rounded-md focus:outline-none focus:border-primary transition";

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <label className="text-sm text-muted-foreground block mb-1.5">{label}</label>
      {children}
    </div>
  );
}
